// src/components/admin/AdminUsage.jsx
// Usage Analytics — Phase 3 (mobile-responsive)

import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';

// ── Helpers ───────────────────────────────────────────────────
const initials = n => n?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
const AVATAR_COLORS = ['var(--gold)', 'var(--blue)', 'var(--green)', '#f472b6', '#a78bfa', 'var(--red)', '#34d399', '#60a5fa'];

function Spinner() {
  return <div style={{ padding: 80, textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>Loading usage data…</div>;
}

// ── Responsive styles ─────────────────────────────────────────
const MOBILE_CSS = `
@media (max-width: 640px) {
  .usage-kpi-grid     { grid-template-columns: repeat(2, 1fr) !important; }
  .usage-middle-row   { grid-template-columns: 1fr !important; }
  .usage-adopt-tabs   { flex-wrap: wrap !important; gap: 0 !important; }
  .usage-adopt-tabs button { flex: 0 0 33.33% !important; height: 34px !important; font-size: 10px !important; }
  .usage-breakdown-grid { grid-template-columns: 1fr !important; }
}
@media (max-width: 420px) {
  .usage-adopt-tabs button { flex: 0 0 50% !important; }
}
`;

function InjectStyle({ id, css }) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ── Stat chip ─────────────────────────────────────────────────
function StatChip({ label, value, color = 'var(--gold)', sub }) {
  return (
    <div style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${color}, transparent 70%)` }}/>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: 7 }}>{label}</div>
      <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

// ── Mini bar chart ────────────────────────────────────────────
function MiniBarChart({ data, color = 'var(--gold)', height = 100, showLabels = true }) {
  const values = Object.values(data);
  const labels = Object.keys(data);
  const max    = Math.max(...values) || 1;
  const step   = Math.ceil(labels.length / 8);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: height + 16, paddingBottom: showLabels ? 18 : 0 }}>
      {values.map((v, i) => {
        const barH      = (v / max) * height;
        const showLabel = showLabels && i % step === 0;
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div
              title={`${labels[i]}: ${v}`}
              style={{ width: '100%', height: barH || 1, background: i === values.length - 1 ? color : `${color}66`, borderRadius: '2px 2px 0 0', transition: 'height .3s ease', minHeight: v > 0 ? 2 : 1 }}
            />
            {showLabel && (
              <span style={{ fontSize: 7, color: 'rgba(247,243,234,.3)', marginTop: 3, transform: 'rotate(-40deg)', transformOrigin: 'top center', whiteSpace: 'nowrap' }}>{labels[i]}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Horizontal bar ────────────────────────────────────────────
function HBar({ name, count, total, color = 'var(--gold)', rank }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 9 }}>
      {rank !== undefined && (
        <span style={{ fontSize: 10, fontWeight: 700, color: rank < 3 ? 'var(--gold)' : 'var(--gray)', width: 16, textAlign: 'right', flexShrink: 0 }}>#{rank + 1}</span>
      )}
      <span style={{ fontSize: 12, color: 'var(--off-white)', width: 100, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,.06)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .5s ease' }}/>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, width: 34, textAlign: 'right', flexShrink: 0 }}>{count}</span>
    </div>
  );
}

// ── Feature adoption bar ──────────────────────────────────────
function AdoptionBar({ feature, pct, count }) {
  const color = pct >= 70 ? 'var(--green)' : pct >= 40 ? 'var(--gold)' : 'rgba(217,79,79,.9)';
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: 'var(--off-white)' }}>{feature}</span>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: 'var(--gray)' }}>{count.toLocaleString()} trades</span>
          <span style={{ fontSize: 11, fontWeight: 700, color }}>{pct}%</span>
        </div>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,.06)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .6s ease' }}/>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function AdminUsage() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [tradeView, setTradeView] = useState('daily');   // daily | monthly
  const [adoptTab,  setAdoptTab]  = useState('symbols'); // symbols | strategies | sessions | emotions | grades | entryTypes

  useEffect(() => {
    adminApi.getUsage()
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner/>;
  if (error)   return <div style={{ padding: 40, color: 'var(--red)', fontSize: 13 }}>Failed to load usage data: {error}</div>;

  const { retention, featureAdoption, topUsers, totalTrades } = data;

  const adoptionData = {
    symbols:    data.symbols,
    strategies: data.strategies,
    sessions:   data.sessions,
    emotions:   data.emotions,
    grades:     data.setupGrades,
    entryTypes: data.entryTypes,
  };
  const adoptionMax = Math.max(...(adoptionData[adoptTab]?.map(d => d.count) || [1])) || 1;

  const adoptTabLabels = {
    symbols: 'Symbols', strategies: 'Strategies', sessions: 'Sessions',
    emotions: 'Emotions', grades: 'Grades', entryTypes: 'Entry',
  };

  return (
    <>
      <InjectStyle id="usage-mobile-css" css={MOBILE_CSS}/>
      <div style={{ padding: '14px 14px 60px', animation: 'pageIn .3s ease', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* KPI strip */}
        <div className="usage-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
          <StatChip label="Total Trades" value={totalTrades.toLocaleString()} color="var(--gold)"  sub="All time"/>
          <StatChip label="DAU"          value={retention.DAU}               color="var(--green)" sub="Active today"/>
          <StatChip label="WAU"          value={retention.WAU}               color="var(--blue)"  sub="Active this week"/>
          <StatChip label="MAU"          value={retention.MAU}               color="var(--gold)"  sub="Active this month"/>
          <StatChip label="DAU / MAU"    value={`${retention.dauMau}%`}      color={retention.dauMau >= 20 ? 'var(--green)' : 'var(--gold)'} sub="Engagement ratio"/>
          <StatChip label="WAU / MAU"    value={`${retention.wauMau}%`}      color={retention.wauMau >= 40 ? 'var(--green)' : 'var(--gold)'} sub="Weekly retention"/>
        </div>

        {/* Trade activity chart */}
        <div className="panel">
          <div className="panel-hd" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div className="panel-title"><b>Trade</b> Activity</div>
            <div style={{ display: 'flex', marginLeft: 'auto' }}>
              {[['daily','Daily (30d)'],['monthly','Monthly (12mo)']].map(([v, l]) => (
                <button
                  key={v} onClick={() => setTradeView(v)}
                  style={{ padding: '0 10px', height: 30, background: tradeView === v ? 'rgba(212,168,67,.1)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.04)', color: tradeView === v ? 'var(--gold)' : 'rgba(247,243,234,.45)', fontSize: 11, fontWeight: tradeView === v ? 700 : 500, cursor: 'pointer', fontFamily: "'Inter',sans-serif", whiteSpace: 'nowrap' }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <MiniBarChart
            data={tradeView === 'daily' ? data.dailyTrades : data.monthlyTrades}
            color="var(--gold)"
            height={120}
            showLabels
          />
        </div>

        {/* Top users + feature adoption */}
        <div className="usage-middle-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 14 }}>

          {/* Top users */}
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Most</b> Active Users</div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>By trade count</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {topUsers.map((u, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: AVATAR_COLORS[i % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: 'var(--black)', flexShrink: 0 }}>
                    {initials(u.user.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.user.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.user.email}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color: i === 0 ? 'var(--gold)' : 'var(--off-white)', lineHeight: 1 }}>{u.tradeCount}</div>
                    <div style={{ fontSize: 9, color: 'var(--gray)' }}>trades</div>
                  </div>
                </div>
              ))}
              {!topUsers.length && <div style={{ color: 'var(--gray)', fontSize: 12 }}>No trades yet.</div>}
            </div>
          </div>

          {/* Feature adoption */}
          <div className="panel">
            <div className="panel-hd">
              <div className="panel-title"><b>Feature</b> Adoption</div>
              <span style={{ fontSize: 11, color: 'var(--gray)' }}>% of trades</span>
            </div>
            {featureAdoption.map(f => (
              <AdoptionBar key={f.feature} feature={f.feature} pct={f.pct} count={f.count}/>
            ))}
            {!featureAdoption.length && <div style={{ color: 'var(--gray)', fontSize: 12 }}>No trade data yet.</div>}
          </div>
        </div>

        {/* Breakdown panel — tabbed */}
        <div className="panel">
          <div className="panel-hd" style={{ flexWrap: 'wrap', gap: 0 }}>
            <div className="panel-title"><b>Usage</b> Breakdown</div>
            <div className="usage-adopt-tabs" style={{ display: 'flex', marginLeft: 'auto' }}>
              {Object.entries(adoptTabLabels).map(([v, l]) => (
                <button
                  key={v} onClick={() => setAdoptTab(v)}
                  style={{ padding: '0 10px', height: 30, background: adoptTab === v ? 'rgba(212,168,67,.1)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.04)', color: adoptTab === v ? 'var(--gold)' : 'rgba(247,243,234,.45)', fontSize: 11, fontWeight: adoptTab === v ? 700 : 500, cursor: 'pointer', fontFamily: "'Inter',sans-serif", letterSpacing: '.02em', whiteSpace: 'nowrap' }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="usage-breakdown-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '2px 24px' }}>
            {(adoptionData[adoptTab] || []).map((item, i) => (
              <HBar key={i} rank={i} name={item.name} count={item.count} total={adoptionMax} color={AVATAR_COLORS[i % AVATAR_COLORS.length]}/>
            ))}
            {!(adoptionData[adoptTab]?.length) && (
              <div style={{ color: 'var(--gray)', fontSize: 12, padding: '10px 0' }}>No data yet for this category.</div>
            )}
          </div>
        </div>

      </div>
    </>
  );
}